/**
 * The pieces the sibling filter documents share — the newer per-entity
 * documents (today AR invoices, AP bills and the bank feed) are all built on
 * these factories so grids that sit alongside each other cannot drift apart in
 * how a filter behaves.
 *
 * INTERNAL to those files. It is deliberately NOT re-exported from
 * `index.ts`: the public surface stays the per-entity vocabulary
 * (`AR_INVOICE_FILTER_*`, `applyARInvoiceFilterDocument`, …) exactly as
 * `placesFilters.ts` and `sustainabilityFilters.ts` present theirs, so a panel
 * never reaches for a generic factory instead of its entity's contract.
 *
 * The two OLDER documents (places, Impact) predate this module and each spell
 * these shapes out inline. They are NOT refactored onto it — that cross-feature
 * dedup is a separate change and is recorded as a follow-up. What this module
 * guarantees today is that every document written on top of it shares one
 * implementation; a new sibling joins by calling the same factories, not by
 * being named here.
 *
 * Every factory keeps its field vocabulary GENERIC and precise: `z.enum` over
 * the caller's `as const` tuple, so `z.infer` still narrows `field` to that
 * entity's literal union (an unknown field string is a type error, not a
 * silently-accepted condition).
 *
 * THREE families live here: strings (`in` / `is_empty`), calendar-date ranges
 * (`date_gte` / `date_lte` / `date_between`) and, since the Vault grid, ARRAYS
 * (`array_has_any` / `array_is_empty` — a `text[]` column such as a file's
 * tags). Their op sets are DISJOINT ON PURPOSE: every entity evaluator narrows
 * on `op` ALONE, so the array family may not reuse the string family's
 * `is_empty` — a shared op name would send a `string[]` accessor's value into
 * `matchesStringOp` and break the switch for whichever entity carries both.
 */
import { z } from "zod";
/** `yyyy-MM-dd` — the only date shape a condition bound may carry. */
export const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "expected yyyy-MM-dd");
/**
 * String condition: `in` = value is one of `values` (exact match);
 * `is_empty` = value unset. `values` entries have `min(1)`, so `''` can never
 * be one of them — an unset value is only ever reachable through `is_empty`
 * or through the `include_empty` widening below.
 *
 * ── WHY `include_empty` AND NOT A SECOND CONDITION ──────────────────────────
 * The panels offer an "(empty)" checkbox ALONGSIDE the real values of a
 * family, and the legacy header popover it replaces treated the two as an OR
 * ("project is Summer Festival OR has no project"). The document envelope is
 * `match: 'all'` — conditions AND together — so expressing that as a separate
 * `is_empty` condition beside the `in` on the SAME field would build a filter
 * that can never match any row. `include_empty: true` widens the ONE `in`
 * condition instead, which also keeps the panel's field → condition mapping
 * one-to-one (no two conditions on a field, so nothing can be lost to the
 * last-wins read in `formFromDocument`).
 *
 * Exactly one spelling per selection state, so the round-trip is canonical:
 *   values only          → { op: 'in', values }
 *   "(empty)" only       → { op: 'is_empty' }
 *   values + "(empty)"   → { op: 'in', values, include_empty: true }
 * `include_empty` on an `is_empty` condition would be a second, redundant
 * spelling of that last state, so it is rejected. `values` on an `is_empty`
 * condition is rejected for the same reason and one worse: the evaluator
 * ignores them, so they read as a live constraint while being dead — and a
 * panel edit that MERGES the condition would resurrect them, silently
 * WIDENING the filter on a family the user never touched.
 */
export function makeStringConditionSchema(fields) {
    return z
        .object({
        field: z.enum(fields),
        op: z.enum(["in", "is_empty"]),
        values: z.array(z.string().min(1).max(200)).min(1).max(50).optional(),
        /** `in` only: ALSO match rows whose value is unset (null / `''`). */
        include_empty: z.boolean().optional(),
    })
        .refine((c) => c.op !== "in" || (c.values?.length ?? 0) > 0, {
        message: "op 'in' requires a non-empty values array",
    })
        .refine((c) => c.op !== "is_empty" || c.include_empty == null, {
        message: "op 'is_empty' already selects empty — drop include_empty",
    })
        .refine((c) => c.op !== "is_empty" || c.values == null, {
        message: "op 'is_empty' selects unset rows — drop values (they are dead)",
    });
}
/**
 * Array condition over a `text[]` row value (today: a file's tags).
 * `array_has_any` = the row's array contains AT LEAST ONE of `values`;
 * `array_is_empty` = the row's array has no entries at all.
 *
 * ── WHY THESE OP NAMES AND NOT `in` / `is_empty` ────────────────────────────
 * Every entity evaluator's `matchesCondition` narrows on `op` ALONE (the
 * disjoint-op-set invariant the sibling documents are built on). Reusing the
 * string family's `is_empty` would still TYPE-CHECK — a union of object
 * shapes — while routing an array-valued accessor into `matchesStringOp`, so
 * the ops are namespaced `array_*` instead.
 *
 * The refines mirror the string family, for the same reasons documented
 * there: `array_has_any` with no values would be a condition that matches
 * nothing (spell "nothing selected" as NO CONDITION), and `values` on an
 * `array_is_empty` condition are DEAD — the evaluator ignores them, so they
 * read as a live constraint, and a panel edit that MERGED the condition would
 * resurrect them and silently widen the filter.
 *
 * There is deliberately no `include_empty` widening here: the legacy Vault
 * grid's tags multiSelect had no "(empty)" checkbox, so the faithful port has
 * no state to spell. Adding one later stays additive.
 */
export function makeArrayConditionSchema(fields) {
    return z
        .object({
        field: z.enum(fields),
        op: z.enum(["array_has_any", "array_is_empty"]),
        values: z.array(z.string().min(1).max(200)).min(1).max(50).optional(),
    })
        .refine((c) => c.op !== "array_has_any" || (c.values?.length ?? 0) > 0, {
        message: "op 'array_has_any' requires a non-empty values array",
    })
        .refine((c) => c.op !== "array_is_empty" || c.values == null, {
        message: "op 'array_is_empty' selects empty arrays — drop values (they are dead)",
    });
}
/**
 * Calendar-date range over `yyyy-MM-dd` strings (lexicographic compare is
 * date order for this format). Distinct ops from any numeric family so an
 * evaluator can narrow on `op` alone — the convention `sustainabilityFilters`
 * set.
 */
export function makeDateConditionSchema(fields) {
    return z
        .object({
        field: z.enum(fields),
        op: z.enum(["date_gte", "date_lte", "date_between"]),
        min: isoDateSchema.optional(),
        max: isoDateSchema.optional(),
    })
        .refine((c) => (c.op === "date_gte" && c.min != null) ||
        (c.op === "date_lte" && c.max != null) ||
        (c.op === "date_between" && c.min != null && c.max != null), { message: "date condition is missing its bound(s)" });
}
/**
 * The document envelope: `version` and `match` are pinned literals (v1 is
 * AND-only; `match` exists so v2 can add 'any' without re-shaping) and the
 * condition array is capped at 20 — the same envelope the places and Impact
 * documents carry.
 */
export function makeFilterDocumentSchema(condition) {
    return z.object({
        version: z.literal(1),
        match: z.literal("all"),
        conditions: z.array(condition).max(20),
    });
}
const CALENDAR_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * The LOCAL calendar day (`yyyy-MM-dd`) a row value falls on, or null when
 * there is nothing to compare.
 *
 * Two branches, mirroring `isValueInDateRange` in the grid primitive that the
 * filter panel replaces — so a converted grid filters the same rows it did as
 * filterable columns:
 *
 *  - Already `yyyy-MM-dd` (every mapper-normalized date column): returned
 *    VERBATIM. No `new Date(...)` round-trip — parsing a date-only string as
 *    an instant is exactly the UTC shift these mappers exist to prevent.
 *  - A raw `timestamptz` (the hidden "Created" column ships the DB value
 *    unmapped): reduced to the day it falls on IN THE VIEWER'S ZONE via local
 *    Date components — the same day `safeFormatDate(value, 'yyyy-MM-dd')`
 *    renders in the cell, and the same day `startOfDay`/`endOfDay` bracketed
 *    in the column filter. Never an ISO prefix slice, which would be the UTC
 *    day and can be off by one either side of midnight.
 */
export function toLocalCalendarDay(value) {
    if (!value)
        return null;
    if (CALENDAR_DATE_RE.test(value))
        return value;
    const instant = new Date(value);
    if (Number.isNaN(instant.getTime()))
        return null;
    const month = String(instant.getMonth() + 1).padStart(2, "0");
    const day = String(instant.getDate()).padStart(2, "0");
    return `${instant.getFullYear()}-${month}-${day}`;
}
/**
 * `in` / `is_empty` semantics — an unset value reads as `''`.
 *
 * The null rule is UNCHANGED by `includeEmpty`: `values` entries are `min(1)`,
 * so `''` can never be a member and a plain "is one of" still sweeps up no
 * empty row. `includeEmpty` is the ONLY way an `in` condition reaches one, and
 * it ORs — "one of these values, OR nothing at all".
 */
export function matchesStringOp(op, value, values, includeEmpty) {
    if (op === "is_empty")
        return value === "";
    return (values ?? []).includes(value) || (includeEmpty === true && value === "");
}
/**
 * `array_has_any` / `array_is_empty` semantics.
 *
 * ANY, NOT ALL: a row matches when it carries at least one of the selected
 * values — exactly what the legacy grid's native array multiSelect did
 * (`filterValue.some(v => tags.includes(v))` in FilesDataGrid.tsx). Requiring
 * EVERY selected value instead would make a two-tag selection return nothing
 * in the common case, which is the whole reason this is spelled out.
 */
export function matchesArrayOp(op, value, values) {
    if (op === "array_is_empty")
        return value.length === 0;
    return (values ?? []).some((v) => value.includes(v));
}
/**
 * Date-range semantics: INCLUSIVE at both bounds, and an unset date matches
 * NO range (a row with no due date is not "due before March").
 */
export function matchesDateOp(op, day, min, max) {
    if (!day)
        return false;
    if (op === "date_gte")
        return min == null || day >= min;
    if (op === "date_lte")
        return max == null || day <= max;
    return (min == null || day >= min) && (max == null || day <= max);
}
//# sourceMappingURL=filterDocumentPrimitives.js.map